import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Select } from '@artosera/ui';
import { centimesDepuisEuros } from '@artosera/shared';
import { ApiError } from '../../lib/api.js';
import { estElevationAnnulee, useElevation } from '../../components/useElevation.js';
import {
  ventesAddonsApi,
  type FactureAddonDetail,
  type SaisieAvoir,
} from '../../lib/addon-ventes.js';

export function AvoirAddon({ facture }: { facture: FactureAddonDetail }) {
  const { t, i18n } = useTranslation('console');
  const client = useQueryClient();
  const elevation = useElevation();
  const [ouverte, setOuverte] = useState(false);
  const [nature, setNature] = useState('total');
  const [montant, setMontant] = useState('');
  const [motif, setMotif] = useState('');
  const [confirmation, setConfirmation] = useState<{
    saisie: SaisieAvoir;
    revisionFacture: number;
    ttc: number;
    motif: string;
  } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const signaler = (e: unknown) => {
    if (e instanceof RangeError) setErreur(t('ventes.montantInvalide'));
    else if (e instanceof ApiError)
      setErreur(
        t(`ventes.erreurs.${(e.details as { raison?: string } | null)?.raison}`, {
          defaultValue: t('ventes.erreur'),
        }),
      );
    else setErreur(t('ventes.erreur'));
  };
  const devis = useMutation({
    retry: false,
    mutationFn: async () => {
      const saisie: SaisieAvoir =
        nature === 'total'
          ? { nature: 'total' }
          : { nature: 'partiel', montantHtCentimes: centimesDepuisEuros(montant) };
      const resultat = await ventesAddonsApi.devisAvoir(facture.id, saisie);
      return {
        saisie,
        revisionFacture: resultat.revisionFacture,
        ttc: resultat.totaux.totalTtcCentimes,
        motif: motif.trim(),
      };
    },
    onMutate: () => setErreur(null),
    onSuccess: setConfirmation,
    onError: signaler,
  });
  const emission = useMutation({
    retry: false,
    mutationFn: () =>
      elevation.executer(async () => {
        if (!confirmation) throw new Error('Devis absent');
        const fraiche = await ventesAddonsApi.facture(facture.id);
        if (fraiche.revision !== confirmation.revisionFacture)
          throw new ApiError(409, 'conflit.version', t('ventes.actualiser'), {
            raison: 'revision_facture_perimee',
          });
        return ventesAddonsApi.avoir(facture.id, {
          ...confirmation.saisie,
          motif: confirmation.motif,
          revisionFacture: confirmation.revisionFacture,
        });
      }),
    onSuccess: () => setOuverte(false),
    onError: (e) => {
      if (estElevationAnnulee(e)) return;
      setConfirmation(null);
      signaler(e);
    },
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['console', 'addon-facture', facture.id] }),
        client.invalidateQueries({ queryKey: ['console', 'addon-ventes'] }),
        client.invalidateQueries({ queryKey: ['console', 'comptabilite'] }),
      ]);
    },
  });
  const attente = devis.isPending || emission.isPending;
  return (
    <>
      <Button
        onClick={() => {
          setConfirmation(null);
          setErreur(null);
          setMotif('');
          setMontant('');
          setNature('total');
          setOuverte(true);
        }}
      >
        {t('ventes.emettreAvoir')}
      </Button>
      <Dialog
        open={ouverte && !elevation.enElevation}
        onOpenChange={(open) => {
          if (!attente) setOuverte(open);
        }}
        title={t('ventes.emettreAvoir')}
        closeLabel={t('ventes.fermer')}
      >
        {confirmation ? (
          <>
            <p>
              {t('ventes.confirmationAvoir', {
                numero: facture.numero,
                montant: new Intl.NumberFormat(i18n.language, {
                  style: 'currency',
                  currency: 'EUR',
                }).format(confirmation.ttc / 100),
              })}
            </p>
            <p>{confirmation.motif}</p>
            <p>{t('ventes.avoirSansDroit')}</p>
            <Button disabled={attente} onClick={() => setConfirmation(null)}>
              {t('ventes.modifierAvoir')}
            </Button>
            <Button disabled={attente} onClick={() => emission.mutate()}>
              {t('ventes.confirmerAvoir')}
            </Button>
          </>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!attente && motif.trim()) devis.mutate();
            }}
          >
            <fieldset disabled={attente}>
              <label>
                {t('ventes.natureAvoir')}
                <Select
                  value={nature}
                  onValueChange={setNature}
                  options={[
                    { value: 'total', label: t('ventes.avoirTotal') },
                    { value: 'partiel', label: t('ventes.avoirPartiel') },
                  ]}
                />
              </label>
              {nature === 'partiel' ? (
                <label>
                  {t('ventes.montantHt')}
                  <Input
                    required
                    inputMode="decimal"
                    pattern="[0-9]+([.,][0-9]{1,2})?"
                    value={montant}
                    onChange={(e) => setMontant(e.target.value)}
                  />
                </label>
              ) : null}
              <label>
                {t('ventes.motifAvoir')}
                <Input
                  required
                  maxLength={500}
                  value={motif}
                  onChange={(e) => setMotif(e.target.value)}
                />
              </label>
              <Button type="submit" disabled={!motif.trim()}>
                {t('ventes.preparerAvoir')}
              </Button>
            </fieldset>
          </form>
        )}
        {erreur ? <p role="alert">{erreur}</p> : null}
      </Dialog>
      {elevation.dialogue}
    </>
  );
}
